import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import TextInputField from 'components/TextInputField';
import MagicButton from 'components/MagicButton/MagicButton';
import { AssetUrls } from 'config/assets';
import { showError, showSuccess } from 'utils/toastNotifications';
import { useValidateResetTokenQuery, useResetPasswordMutation } from 'store/services/auth/authApi';
import { loginSuccess } from 'store/services/auth/authSlice';
import './PasswordResetPage.css';

const PasswordResetPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [confirmPasswordError, setConfirmPasswordError] = useState(false);
  const [showRequirements, setShowRequirements] = useState(false);
  
  const { data: tokenData, isLoading: isValidating, error: tokenError } = useValidateResetTokenQuery(token || '', {
    skip: !token,
  });
  
  const [resetPassword, { isLoading: isResetting }] = useResetPasswordMutation();

  useEffect(() => {
    if (tokenError || (tokenData && !tokenData.valid)) {
      showError('This reset link has expired or is invalid. Please request a new one.');
      setTimeout(() => navigate('/welcome'), 3000);
    }
  }, [tokenError, tokenData, navigate]);

  const passwordRequirements = [
    { regex: /.{8,}/, text: 'At least 8 characters' },
    { regex: /[A-Z]/, text: 'One uppercase letter' },
    { regex: /[a-z]/, text: 'One lowercase letter' },
    { regex: /[0-9]/, text: 'One number' },
    { regex: /[!@#$%^&*]/, text: 'One special character (!@#$%^&*)' },
  ];

  const validatePassword = (password: string): boolean => {
    return passwordRequirements.every(req => req.regex.test(password));
  };

  const getPasswordStrength = (password: string): number => {
    return passwordRequirements.filter(req => req.regex.test(password)).length;
  };

  const handleSubmit = async () => {
    let hasError = false;

    if (!validatePassword(password)) {
      setPasswordError(true);
      showError('Password does not meet all requirements');
      hasError = true;
    }

    if (password !== confirmPassword) {
      setConfirmPasswordError(true);
      showError('Passwords do not match');
      hasError = true;
    }

    if (hasError || !token) return;

    try {
      const result = await resetPassword({
        token,
        newPassword: password,
      }).unwrap();

      showSuccess('Password reset successful! Logging you in...');

      // Auto-login with the returned token
      dispatch(loginSuccess({
        token: result.token,
        csrfToken: result.csrfToken,
        user: result.user
      }));

      // Navigate to dashboard
      setTimeout(() => navigate('/my-cards'), 1000);
    } catch (error: any) {
      if (error?.data?.message) {
        showError(error.data.message);
      } else {
        showError('Failed to reset password. Please try again.');
      }
    }
  };

  if (isValidating) {
    return (
      <div className="password-reset-page">
        <div className="password-reset-container">
          <div className="password-reset-loading">
            <div className="password-reset-spinner"></div>
            <p>Validating reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenData?.valid) {
    return null; // Will redirect in useEffect
  }

  const passwordStrength = getPasswordStrength(password);

  return (
    <div className="password-reset-page">
      <div className="password-reset-container">
        <div className="password-reset-logo">
          <img src={AssetUrls.logos.whiteSvg} alt="MagicTrade" />
        </div>
        
        <h1 className="password-reset-title">Reset Your Password</h1>
        
        {tokenData.email && (
          <p className="password-reset-email">
            Resetting password for: <strong>{tokenData.email}</strong>
          </p>
        )}

        <div className="password-reset-form">
          <div className="password-reset-field">
            <TextInputField
              label="New Password"
              type="password"
              value={password}
              error={passwordError}
              onChange={(value) => {
                setPassword(value);
                if (passwordError) setPasswordError(false);
                setShowRequirements(true);
              }}
            />
            
            {(showRequirements || password) && (
              <div className="password-requirements">
                <div className="password-strength">
                  <div className="password-strength-bar">
                    <div 
                      className={`password-strength-fill strength-${passwordStrength}`}
                      style={{ width: `${(passwordStrength / 5) * 100}%` }}
                    />
                  </div>
                  <span className="password-strength-label">
                    {passwordStrength === 0 && 'Very Weak'}
                    {passwordStrength === 1 && 'Weak'}
                    {passwordStrength === 2 && 'Fair'}
                    {passwordStrength === 3 && 'Good'}
                    {passwordStrength === 4 && 'Strong'}
                    {passwordStrength === 5 && 'Very Strong'}
                  </span>
                </div>
                <ul className="password-requirements-list">
                  {passwordRequirements.map((req, index) => (
                    <li
                      key={index}
                      className={req.regex.test(password) ? 'requirement-met' : ''}
                    >
                      <span className="requirement-icon">
                        {req.regex.test(password) ? '✓' : '○'}
                      </span>
                      {req.text}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="password-reset-field">
            <TextInputField
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              error={confirmPasswordError}
              onChange={(value) => {
                setConfirmPassword(value);
                if (confirmPasswordError) setConfirmPasswordError(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSubmit();
              }}
            />
            {confirmPassword && password !== confirmPassword && (
              <div className="password-mismatch">
                Passwords do not match
              </div>
            )}
          </div>

          <MagicButton
            label="Reset Password"
            color="purple"
            onPress={handleSubmit}
            isLoading={isResetting}
            isDisabled={!password || !confirmPassword || isResetting}
          />

          <div className="password-reset-links">
            <a href="/welcome" className="password-reset-link">
              Back to Login
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetPage;